import { useEffect, useState } from "react";
import classNames from "classnames";
import { ComponentLoader } from "./Loader";
import styles from "./styles.module.scss";
import { NodeTypeIcon } from "./Column";
import { useReactFlow } from "reactflow";
import { useLineageContext } from "../context/LineageProvider";
import { FunctionMetaData } from "../types";
import { getFunctionDetails } from "../api";
import PurposeSection from "./Purpose";
import ColumnRow from "./ColumnRow";
import { Stack } from "@altimateai/lego";

const FunctionDetails = () => {
  const flow = useReactFlow();
  const {
    state: { selectedTable },
  } = useLineageContext();
  const [data, setData] = useState<FunctionMetaData | null>(null);
  const selectedTableData = flow.getNode(selectedTable)?.data;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedTable) return;
    setIsLoading(true);
    setError(null);
    getFunctionDetails(selectedTable)
      .then(_data => {
        setData(_data);
        setIsLoading(false);
      })
      .catch(err => {
        setError(String(err?.message || err));
        setIsLoading(false);
      });
  }, [selectedTable]);

  if (isLoading) return <ComponentLoader />;
  if (error || !data || !selectedTable) {
    return (
      <Stack direction="column" gap="md" className="al-p-4">
        <div className="al-text-sm al-text-destructive-foreground">
          {error ? `Failed to load function details: ${error}` : "No data available"}
        </div>
      </Stack>
    );
  }

  return (
    <Stack direction="column" gap="md" className="al-p-2 al-h-full al-overflow-y-auto">
      <div className={styles.table_details_header}>
        <NodeTypeIcon nodeType={selectedTableData.nodeType} />
        <Stack direction="row" align="center">
          <div className="fw-semibold fs-5 lines-2">{selectedTableData.label}</div>
        </Stack>
      </div>
      {data.description ? <PurposeSection purpose={data.description} /> : null}
      <div className={classNames(styles.card, "flex-grow column-section")}>
        {data.config?.type && <ColumnRow title="Type" value={data.config.type} />}
        {data.config?.volatility && <ColumnRow title="Volatility" value={data.config.volatility} />}
        {data.config?.runtime_version && (
          <ColumnRow title="Runtime" value={data.config.runtime_version} />
        )}
        {data.config?.entry_point && (
          <ColumnRow title="Entry Point" value={data.config.entry_point} />
        )}
        {data.package_name && <ColumnRow title="Package" value={data.package_name} />}
        {data.returns && (
          <ColumnRow
            title="Returns"
            value={`${data.returns.data_type}${data.returns.description ? ` — ${data.returns.description}` : ""}`}
          />
        )}
        {data.arguments && data.arguments.length > 0 && (
          <ColumnRow
            title="Arguments"
            value={data.arguments
              .map(arg => {
                let s = `${arg.name}: ${arg.data_type}`;
                if (arg.default_value) s += ` = ${arg.default_value}`;
                return s;
              })
              .join(", ")}
          />
        )}
      </div>
    </Stack>
  );
};

export default FunctionDetails;
